import { useMemo } from "react";

import { EntityId } from "@reduxjs/toolkit";

import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";

import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import InventoryIcon from "@mui/icons-material/Inventory";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";

import { Dossier } from "types/Dossier";
import { DeviceList, DeviceListToggleMode } from "features/devices/DeviceList";
import { DossierDeviceListVisibilityToggle } from "./DossierDeviceListVisibilityToggle";
import { caseViewerActions } from "./store/slice";
import { playbackSelectors } from "./store/selectors.playback";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReIdSidebarControls } from "./reId/ReIdSidebarControls";
import { DossierCapabilityName } from "types/DossierCapabilityName";

export interface SidebarProps {
    onEditDossier: () => void;
    dossier?: Dossier;
}

export function Sidebar(props: SidebarProps) {
    const dispatch = useAppDispatch();
    const { dossier } = props;

    const visibleDossierDeviceIds = useAppSelector(
        playbackSelectors.selectVisibleDossierDeviceIds,
    );

    const orderedDevices = useMemo(() => {
        return props.dossier
            ? props.dossier.dossierDevices
                  .filter((x) => x.isSelected)
                  .toSorted((a, b) => a.name.localeCompare(b.name))
            : [];
    }, [props.dossier]);

    return (
        dossier && (
            <Box
                sx={{
                    overflow: "auto",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <InventoryIcon />
                        </ListItemIcon>
                        <ListItemText primary={dossier.name} />
                        <IconButton onClick={props.onEditDossier}>
                            <EditIcon />
                        </IconButton>
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <FitnessCenterOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={dossier.siteId} />
                    </ListItem>

                    {dossier.dossierCapabilityNames.some(
                        (x) => x === DossierCapabilityName.ReIdentification,
                    ) && <ReIdSidebarControls dossierId={dossier.id} />}

                    <ListItem>
                        <ListItemIcon>
                            <PhotoCamera />
                        </ListItemIcon>
                        <ListItemText primary="Devices" />
                    </ListItem>
                    <DeviceList
                        onToggleDevice={(
                            dossierDeviceId: EntityId,
                            visible: boolean,
                        ) =>
                            dispatch(
                                caseViewerActions.upsertVisibleDossierDeviceIdState(
                                    { dossierDeviceId, visible },
                                ),
                            )
                        }
                        devices={orderedDevices}
                        selectedDeviceIds={visibleDossierDeviceIds ?? []}
                        onToggleSelectAll={(visible: boolean) =>
                            dispatch(
                                caseViewerActions.setVisibilityAllDossierDevices(
                                    {
                                        visible,
                                    },
                                ),
                            )
                        }
                        thumbnailRefreshIntervalInMs={0}
                        hideDeviceThumbnail
                        deviceListToggleMode={DeviceListToggleMode.Visibility}
                        deviceListVisibilityToggleComponent={
                            DossierDeviceListVisibilityToggle
                        }
                    />
                </List>
            </Box>
        )
    );
}
